"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { WoozooEyeLogo } from "./WoozooLogo";
import { useI18n } from "@/i18n/context";

export function HeroSection() {
  const { t } = useI18n();
  const [scrollY, setScrollY] = useState(0);
  const [phase, setPhase] = useState<"hidden" | "entering" | "ready">("hidden");

  useEffect(() => {
    // Phase 1: trigger entrance
    const t1 = setTimeout(() => setPhase("entering"), 100);
    // Phase 2: entrance done, kill transitions so scroll is instant
    const t2 = setTimeout(() => setPhase("ready"), 2000);

    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const eyeOffset = scrollY * -0.4;
  const titleOffset = scrollY * -0.15;
  const entering = phase === "entering";
  const visible = phase !== "hidden";
  const transition = phase !== "ready";

  return (
    <section
      id="home"
      className="relative min-h-screen bg-bg-primary flex items-center justify-center"
    >
      <div className="flex flex-col items-center gap-[38px] max-w-[1013px] px-8">
        {/* Eye */}
        <div
          className={transition ? "transition-all duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)]" : ""}
          style={{
            position: "relative",
            top: `${eyeOffset}px`,
            opacity: visible ? 1 : 0,
            transform: entering || !transition ? "translateY(0)" : "translateY(-60px)",
          }}
        >
          <WoozooEyeLogo className="w-[416px] h-[413px]" />
        </div>

        {/* Tagline */}
        <h1
          className={`font-clash font-bold text-[38px] text-text-primary text-center uppercase tracking-[2.66px] leading-normal ${transition ? "transition-all duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] delay-[200ms]" : ""}`}
          style={{
            position: "relative",
            top: `${titleOffset}px`,
            opacity: visible ? 1 : 0,
            transform: entering || !transition ? "translateY(0)" : "translateY(40px)",
          }}
        >
          {t.hero.tagline}
        </h1>

        {/* Subtitle */}
        <p
          className={`font-besley text-[24px] text-text-secondary text-center tracking-[1.68px] leading-[28px] max-w-[871px] ${transition ? "transition-all duration-[1s] ease-[cubic-bezier(0.16,1,0.3,1)] delay-[500ms]" : ""}`}
          style={{
            opacity: visible ? 1 : 0,
            transform: entering || !transition ? "translateY(0)" : "translateY(30px)",
          }}
        >
          {t.hero.subtitle}
        </p>

        {/* Store badges */}
        <div
          className={`flex gap-0 ${transition ? "transition-all duration-[0.8s] ease-[cubic-bezier(0.16,1,0.3,1)] delay-[750ms]" : ""}`}
          style={{
            opacity: visible ? 1 : 0,
            transform: entering || !transition ? "translateY(0)" : "translateY(20px)",
          }}
        >
          <Image
            src="/images/store-badges.png"
            alt="Download on the App Store and Google Play"
            width={230}
            height={37}
            className="h-[37px] w-auto"
          />
        </div>
      </div>
    </section>
  );
}
